import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE_URL = "https://slateblue-mallard-430836.hostingersite.com/api";

const useTicketOptions = () => {
  const [options, setOptions] = useState({
    services: [],
    materials: [],
    locations: [],
    cities: [],
    subjects: [],
    descriptions: [],
    customerNames: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Decode token function (adapted from SaveData for consistency)
  const decodeToken = () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No token found");
        return null;
      }

      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error("Invalid token format");
        return null;
      }

      const payload = JSON.parse(atob(parts[1]));

      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.error("Token expired");
        return null;
      }

      let effectiveCompanyId;
      if (payload.role === 'company') {
        effectiveCompanyId = payload.id;
      } else {
        effectiveCompanyId = payload.companyId;
      }

      if (!effectiveCompanyId) {
        console.error("No company ID in token");
        return null;
      }

      return {
        companyId: effectiveCompanyId,
        role: payload.role,
        userId: payload.id,
        email: payload.email,
        name: payload.name || payload.username,
        exp: payload.exp
      };
    } catch (error) {
      console.error("Token decode error:", error);
      return null;
    }
  };

  const fetchOptions = async () => {
    setLoading(true);
    setError("");

    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      setLoading(false);
      return;
    }

    const userData = decodeToken();
    if (!userData || !userData.companyId) {
      setError("Company ID not found in token. Please login again.");
      setLoading(false);
      return;
    }

    const { companyId } = userData;

    try {
      const res = await fetch(`${API_BASE_URL}/ticket-options/${companyId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        if (res.status === 401) {
          setError("Session expired. Please login again.");
        } else if (res.status === 404) {
          // No options found for company - set empty with customerNames
          setOptions({
            services: [],
            materials: [],
            locations: [],
            cities: [],
            subjects: [],
            descriptions: [],
            customerNames: [] // Initialize customerNames as empty array
          });
          setError(""); // Clear error for 404, as it's valid (empty data)
        } else {
          throw new Error(`Failed to fetch options: ${res.status} ${res.statusText}`);
        }
        return;
      }

      const data = await res.json();

      if (data.success && data.options) {
        // Filter out empty strings and clean the data, including customerNames
        const cleanOptions = {};
        Object.keys(data.options).forEach(key => {
          cleanOptions[key] = data.options[key]
            .filter(item => item && item.trim() !== "")
            .map(item => item.trim().replace(/,$/, '')); // Remove trailing commas
        });

        // Ensure customerNames exists in the cleaned options
        setOptions({
          services: cleanOptions.services || [],
          materials: cleanOptions.materials || [],
          locations: cleanOptions.locations || [],
          cities: cleanOptions.cities || [],
          subjects: cleanOptions.subjects || [],
          descriptions: cleanOptions.descriptions || [],
          customerNames: cleanOptions.customerNames || [] // Handle customerNames
        });
        setError(""); // Clear any previous errors
      } else {
        // No success/options - set empty with customerNames
        setOptions({
          services: [],
          materials: [],
          locations: [],
          cities: [],
          subjects: [],
          descriptions: [],
          customerNames: [] // Initialize customerNames as empty array
        });
        setError("No options available for your company.");
      }
    } catch (err) {
      console.error("Error fetching options:", err);
      setError(err.message || "Failed to load suggestions. Please try refreshing.");
    } finally {
      setLoading(false);
    }
  };

  return { options, loading, error, fetchOptions };
};

// Autocomplete Input Component (unchanged)
const AutocompleteInput = ({
  value,
  onChange,
  suggestions,
  placeholder,
  label,
  onSuggestionSelect,
  ...props
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  useEffect(() => {
    if (value && suggestions.length > 0) {
      const filtered = suggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered.slice(0, 5)); // Show top 5 matches
    } else {
      setFilteredSuggestions(suggestions.slice(0, 5)); // Show recent 5 when empty
    }
  }, [value, suggestions]);

  const handleSuggestionClick = (suggestion) => {
    onChange({ target: { value: suggestion } });
    if (onSuggestionSelect) onSuggestionSelect(suggestion);
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      {label && (
        <label className="block font-medium mb-1 text-gray-700">
          {label}
        </label>
      )}
      <input
        type="text"
        value={value}
        onChange={onChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        placeholder={placeholder}
        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
        {...props}
      />

      {showSuggestions && filteredSuggestions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto cs-autocomplete-dropdown"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors"
              onMouseDown={() => handleSuggestionClick(suggestion)}
            >
              <span className="text-gray-700">{suggestion}</span>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

// Main component that handles both creating and viewing tickets
function CustomerSupportSystem() {
  const [activeTab, setActiveTab] = useState("create"); // "create" or "view"

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalTickets: 0
  });

  // Inside CustomerSupportSystem component, after the state declarations
  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on load
  }, []);

  const mobileStyles = `
    @media (max-width: 768px) {
      .cs-outer-wrapper {
        padding-left: 0px !important;
        padding-right: 0px !important;
        padding-top: 8px !important;
        padding-bottom: 24px !important;
      }
      .cs-outer-container {
        max-width: 100% !important;
        padding-left: 0px !important;
        padding-right: 0px !important;
        margin-left: 0px !important;
        margin-right: 0px !important;
      }
      .cs-tabs-container {
        background-color: transparent !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin-bottom: 12px !important;
        border-radius: 0px !important;
      }
      .cs-tabs-flex {
        background: #1e3a8a !important;
        border-radius: 10px !important;
        padding: 5px !important;
        gap: 5px !important;
        margin: 0px 8px !important;
      }
      .cs-tab-btn {
        background: transparent !important;
        color: #93c5fd !important;
        border-radius: 6px !important;
        padding: 8px !important;
        font-size: 13px !important;
      }
      .cs-tab-btn.active {
        background: #3b82f6 !important;
        color: white !important;
      }
      .cs-tab-btn > div {
        display: none !important;
      }

      /* Create Ticket Form Card */
      .cs-page-card {
        border-radius: 0px !important;
        padding: 12px 14px !important;
        box-shadow: none !important;
        border-left: none !important;
        border-right: none !important;
      }
      .cs-page-card h2 {
        font-size: 17px !important;
        margin-bottom: 12px !important;
        font-weight: 800 !important;
      }
      
      /* Scale inputs and labels down */
      .cs-page-card label,
      .cs-ticket-card form label {
        font-size: 11px !important;
        font-weight: 700 !important;
        margin-bottom: 4px !important;
        color: #4b5563 !important;
      }
      
      .cs-page-card input[type="text"],
      .cs-page-card textarea,
      .cs-ticket-card form input[type="text"],
      .cs-ticket-card form textarea {
        font-size: 12px !important;
        padding: 8px 10px !important;
        border-radius: 8px !important;
      }
      
      .cs-page-card textarea,
      .cs-ticket-card form textarea {
        height: 70px !important;
        min-height: 70px !important;
      }
      
      .cs-page-card form > * + *,
      .cs-ticket-card form > * + * {
        margin-top: 8px !important;
      }
      
      /* Make Autocomplete suggestions compact */
      .cs-autocomplete-dropdown {
        max-height: 180px !important;
      }
      .cs-autocomplete-dropdown div {
        padding: 6px 12px !important;
        font-size: 12px !important;
      }
      
      /* Checkbox options compact */
      .cs-page-card .flex.gap-6 {
        gap: 16px !important;
        margin-top: 4px !important;
        margin-bottom: 4px !important;
      }
      .cs-page-card label.flex.items-center.gap-2 {
        font-size: 12px !important;
        font-weight: 600 !important;
      }
      
      /* Image upload area compact */
      .cs-page-card .h-32 {
        height: 60px !important;
      }
      .cs-page-card .h-32 svg {
        width: 20px !important;
        height: 20px !important;
        margin-bottom: 2px !important;
      }
      .cs-page-card .h-32 p {
        font-size: 10px !important;
        margin-bottom: 0px !important;
        margin-top: 0px !important;
      }
      .cs-page-card .h-32 p span {
        font-size: 10px !important;
      }
      
      /* Selected images preview list */
      .cs-page-card .mt-3 {
        margin-top: 6px !important;
      }
      .cs-page-card .space-y-2 > * + * {
        margin-top: 4px !important;
      }
      .cs-page-card .space-y-2 div {
        padding: 4px 8px !important;
        font-size: 11px !important;
      }
      
      /* Submit button compact */
      .cs-page-card button[type="submit"] {
        padding: 10px 14px !important;
        font-size: 13px !important;
        font-weight: 700 !important;
        border-radius: 8px !important;
      }

      /* My Tickets - Header & Filters */
      .cs-tickets-card {
        border-radius: 0px !important;
        box-shadow: none !important;
        border-left: none !important;
        border-right: none !important;
      }
      .cs-tickets-header {
        flex-direction: column !important;
        align-items: stretch !important;
        padding: 12px 14px !important;
      }
      .cs-tickets-header h2 {
        font-size: 17px !important;
        font-weight: 800 !important;
        margin-bottom: 8px !important;
      }
      .cs-refresh-btn {
        width: 100% !important;
        text-align: center !important;
        justify-content: center !important;
        font-size: 12px !important;
        padding: 8px 12px !important;
        border-radius: 8px !important;
      }
      .cs-filters-wrap {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 6px !important;
        margin-top: 10px !important;
      }
      .cs-filter-btn {
        flex: 1 1 calc(33.333% - 6px) !important;
        text-align: center !important;
        justify-content: center !important;
        font-size: 11px !important;
        padding: 6px 3px !important;
        white-space: nowrap !important;
        border-radius: 6px !important;
      }
      
      .cs-tickets-card .p-6 {
        padding: 12px 14px !important;
      }

      /* Ticket Cards */
      .cs-ticket-card {
        padding: 12px !important;
        border-radius: 8px !important;
        margin-bottom: 12px !important;
        box-shadow: 0 2px 4px rgba(0,0,0,0.02) !important;
        border: 1px solid #f3f4f6 !important;
        background: white !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .cs-ticket-card h4 {
        font-size: 14px !important;
        font-weight: 700 !important;
      }
      .cs-ticket-header {
        display: flex !important;
        flex-direction: column !important;
      }
      .cs-ticket-title-row {
        display: flex !important;
        flex-direction: column !important;
      }
      .cs-ticket-actions {
        display: flex !important;
        flex-wrap: wrap !important;
        gap: 6px !important;
        margin-top: 10px !important;
        width: 100% !important;
      }
      .cs-ticket-actions button {
        flex: 1 1 auto !important;
        text-align: center !important;
        font-size: 12px !important;
        padding: 6px 10px !important;
        border-radius: 6px !important;
      }
    }
  `;


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-6 px-2 sm:px-4 lg:px-4 cs-outer-wrapper">
      <style>{mobileStyles}</style>
      <div className="max-w-[98%] mx-auto cs-outer-container">
        {/* Tab Navigation */}
        <motion.div
          className="bg-white shadow-lg rounded-xl mb-8 overflow-hidden cs-tabs-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="flex cs-tabs-flex">
            <button
              onClick={() => setActiveTab("create")}
              className={`flex-1 py-4 px-6 text-center font-medium transition-all relative cs-tab-btn ${activeTab === "create"
                ? "text-blue-600 active"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              {activeTab === "create" && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeTab"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              Create Ticket
            </button>
            <button
              onClick={() => setActiveTab("view")}
              className={`flex-1 py-4 px-6 text-center font-medium transition-all relative cs-tab-btn ${activeTab === "view"
                ? "text-blue-600 active"
                : "text-gray-600 hover:text-gray-800"
                }`}
            >
              {activeTab === "view" && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600"
                  layoutId="activeTab"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
              My Tickets
            </button>
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "create" && (
            <motion.div
              key="create"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <CustomerSupport onTicketCreated={() => setActiveTab("view")} />
            </motion.div>
          )}
          {activeTab === "view" && (
            <motion.div
              key="view"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <CustomerTickets />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Create Ticket Component
function CustomerSupport({ onTicketCreated }) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);

  const [material, setMaterial] = useState("");
  const [service, setService] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]); // ✅ track material/service selection
  const [location, setLocation] = useState(""); // ✅ New field
  const [city, setCity] = useState(""); // ✅ New field
  const { options, loading: optionsLoading, error: optionsError, fetchOptions } = useTicketOptions();

  // Fetch options on component mount
  useEffect(() => {
    fetchOptions();
  }, []);

  // Helper function to decode JWT token
  const decodeToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  const handleCheckboxChange = (option) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  // Upload multiple images to Cloudinary (unchanged)
  // const uploadToCloudinary = async (files) => {
  //   const uploadedUrls = [];

  //   for (const file of files) {
  //     const formData = new FormData();
  //     formData.append("file", file);
  //     formData.append("upload_preset", "my_unsigned");

  //     try {
  //       const res = await fetch(
  //         "https://api.cloudinary.com/v1_1/dk1kj8b8v/image/upload",
  //         {
  //           method: "POST",
  //           body: formData,
  //         }
  //       );
  //       const data = await res.json();
  //       if (data.secure_url) {
  //         uploadedUrls.push(data.secure_url);
  //       } else {
  //         console.error("Upload failed for file:", file.name);
  //       }
  //     } catch (error) {
  //       console.error("Error uploading to Cloudinary:", error);
  //     }
  //   }

  //   return uploadedUrls;
  // };

  // Handle file selection (unchanged)
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(prevImages => [...prevImages, ...files]);
  };

  // Remove image from selection (unchanged)
  const removeImage = (index) => {
    setImages(prevImages => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    // ✅ Frontend validation
    if (!subject.trim()) {
      setMessage("Please enter the nature of the problem ❌");
      setLoading(false);
      return;
    }

    if (!description.trim()) {
      setMessage("Please provide details of the problem ❌");
      setLoading(false);
      return;
    }

    if (!customerName.trim()) {
      setMessage("Please enter the customer name ❌");
      setLoading(false);
      return;
    }

    if (!/^[A-Za-z\s]+$/.test(customerName.trim())) {
      setMessage("Customer name must contain only alphabets ❌");
      setLoading(false);
      return;
    }

    if (!customerMobile.trim() || customerMobile.length !== 10) {
      setMessage("Please enter a valid 10-digit mobile number ❌");
      setLoading(false);
      return;
    }

    if (!location.trim()) {
      setMessage("Please enter the department ❌");
      setLoading(false);
      return;
    }

    if (!city.trim()) {
      setMessage("Please enter the city ❌");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      alert("Access token not found. Please login.");
      setLoading(false);
      return;
    }

    try {
      const decodedToken = decodeToken(token);
      const customerId = decodedToken?.id;
      if (!customerId) throw new Error("Customer ID not found in token");

      // Create FormData instead of JSON
      const formData = new FormData();

      // Append all text fields
      formData.append('subject', subject);
      formData.append('description', description);
      formData.append('customerId', customerId);
      formData.append('customerName', customerName);
      formData.append('customerMobile', customerMobile);
      formData.append('location', location);
      formData.append('city', city);

      // Append optional fields if they exist
      if (selectedOptions.includes("material") && material) {
        formData.append('material', material);
      }
      if (selectedOptions.includes("service") && service) {
        formData.append('service', service);
      }

      // Append images to FormData
      if (images.length > 0) {
        images.forEach((image, index) => {
          formData.append('image', image); // This will be handled by multer in backend
        });
      }

      // Send as FormData instead of JSON
      const res = await fetch(`${API_BASE_URL}/support-ticket`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type - let browser set it with boundary
        },
        body: formData,
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || `Failed: ${res.status} ${res.statusText}`);
      }

      const result = await res.json();

      setMessage("Ticket created successfully ✅");

      // Reset form
      setSubject("");
      setDescription("");
      setImages([]);
      setCustomerName("");
      setCustomerMobile("");
      setMaterial("");
      setService("");
      setLocation("");
      setCity("");
      setSelectedOptions([]);

      setTimeout(() => {
        if (onTicketCreated) onTicketCreated();
      }, 2000);
    } catch (err) {
      console.error("Error submitting ticket:", err);
      setMessage(`Error creating support ticket: ${err.message} ❌`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="w-full mx-auto"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100 cs-page-card">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Create Support Ticket
        </h2>

        {/* Options Loading/Error Display */}
        <AnimatePresence>
          {optionsLoading && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-center text-blue-700 text-sm"
            >
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                Loading suggestions...
              </div>
            </motion.div>
          )}
          {optionsError && !optionsLoading && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-center text-red-700 text-sm"
            >
              {optionsError}
              <button
                onClick={fetchOptions}
                className="ml-2 underline hover:text-red-900"
              >
                Retry
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <AutocompleteInput
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              suggestions={options.subjects}
              placeholder="Brief description of your issue"
              label="Nature Of Problem*"
              required
            />
          </motion.div>

          {/* Description */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative">
            <label className="block font-medium mb-1 text-gray-700">
              Details Of Problem*
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={() => {
                setIsDescriptionFocused(true);  // NEW: Show suggestions on focus
                console.log('Debug: Textarea focused. Available descriptions:', options.descriptions);  // Keep for debug if needed
              }}
              onBlur={() => {
                // NEW: Hide after delay to allow clicking suggestions
                setTimeout(() => setIsDescriptionFocused(false), 200);
              }}
              required
              rows="5"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-vertical pr-8"  // pr-8 for dropdown space
              placeholder="Provide detailed information about your issue (click here for suggestions)"
            />

            {/* NEW: Indicator if no descriptions (show always, but only if not loading) */}
            {options.descriptions.length === 0 && !optionsLoading && !isDescriptionFocused && (
              <p className="mt-1 text-xs text-gray-500 italic">
                No description templates available. Type freely or check with admin.
              </p>
            )}

            {/* Custom Suggestion Dropdown: Only show when focused */}
            {isDescriptionFocused && options.descriptions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}  // NEW: Animate hide on blur
                className="absolute z-20 w-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {options.descriptions
                  .filter(desc =>
                    !description.trim() || desc.toLowerCase().includes(description.toLowerCase().trim())  // Show all if empty, filter if typing
                  )
                  .slice(0, 5) // Top 5 matches
                  .map((suggestion, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="px-4 py-2 cursor-pointer hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => {
                        e.preventDefault();  // Prevent blur from hiding dropdown
                        const appendText = description.trim() === ''
                          ? suggestion  // Replace if empty
                          : `${description}\n${suggestion}`;  // Append as new line
                        setDescription(appendText);
                        // Optional: Keep focused after selection (or setIsDescriptionFocused(false) to hide)
                      }}
                    >
                      <span className="text-gray-700 text-sm">{suggestion}</span>
                    </motion.div>
                  ))}
                {options.descriptions.filter(desc =>
                  !description.trim() || desc.toLowerCase().includes(description.toLowerCase().trim())
                ).length === 0 && description.trim() && (
                    <p className="px-4 py-2 text-xs text-gray-500 italic">No matching descriptions found.</p>
                  )}
              </motion.div>
            )}
          </motion.div>




          {/* Customer Name */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <AutocompleteInput
              value={customerName}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only letters and spaces
                if (/^[A-Za-z\s]*$/.test(value)) {
                  setCustomerName(value);
                }
              }}
              required
              suggestions={options.customerNames}
              placeholder="Enter customer name"
              label="Customer Name*"
            />
            {customerName && !/^[A-Za-z\s]+$/.test(customerName) && (
              <p style={{ color: "red", fontSize: "0.8rem" }}>
                Only alphabets are allowed.
              </p>
            )}
          </motion.div>

          {/* Customer Mobile */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <label className="block font-medium mb-1 text-gray-700">
              Customer Mobile*
            </label>
            <input
              type="text"
              value={customerMobile}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and limit to 10 digits
                if (/^\d{0,10}$/.test(value)) {
                  setCustomerMobile(value);
                }
              }}
              required
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter customer mobile number"
            />
            {customerMobile && customerMobile.length !== 10 && (
              <p style={{ color: "red", fontSize: "0.8rem" }}>
                Mobile number must be exactly 10 digits.
              </p>
            )}
          </motion.div>

          {/* Address */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <AutocompleteInput
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              suggestions={options.locations}
              placeholder="Enter customer location"
              label="Location*"
              required
            />
          </motion.div>

          {/* City */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <AutocompleteInput
              value={city}
              onChange={(e) => setCity(e.target.value)}
              suggestions={options.cities}
              placeholder="Enter customer city"
              label="City*"
              required
            />
          </motion.div>

          {/* ✅ Checkbox Options */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <label className="block font-medium mb-2 text-gray-700">
              Select Options
            </label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedOptions.includes("material")}
                  onChange={() => handleCheckboxChange("material")}
                  className="w-4 h-4"
                />
                Material
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedOptions.includes("service")}
                  onChange={() => handleCheckboxChange("service")}
                  className="w-4 h-4"
                />
                Service
              </label>
            </div>
          </motion.div>

          {/* ✅ Material Field */}
          {selectedOptions.includes("material") && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <AutocompleteInput
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                suggestions={options.materials}
                placeholder="Enter material details"
                label="Material"
              />
            </motion.div>
          )}

          {/* ✅ Service Field */}
          {selectedOptions.includes("service") && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <AutocompleteInput
                value={service}
                onChange={(e) => setService(e.target.value)}
                suggestions={options.services}
                placeholder="Enter service details"
                label="Service"
              />
            </motion.div>
          )}

          {/* Image Upload - Multiple (unchanged) */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <label className="block font-medium mb-1 text-gray-700">
              Upload Images (Optional, Multiple)
            </label>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg className="w-8 h-8 mb-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 10MB each)</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </div>

            {/* Selected images preview */}
            {images.length > 0 && (
              <motion.div
                className="mt-3"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-sm text-gray-600 mb-2">
                  Selected images ({images.length}):
                </p>
                <div className="space-y-2">
                  {images.map((image, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <span className="text-sm text-gray-600 truncate">
                        {image.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="ml-2 text-red-500 hover:text-red-700 text-sm transition"
                      >
                        Remove
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Submit Button */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <motion.button
              type="submit"
              disabled={loading || optionsLoading}
              whileHover={{ scale: loading || optionsLoading ? 1 : 1.02 }}
              whileTap={{ scale: loading || optionsLoading ? 1 : 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg disabled:opacity-50"
            >
              {loading ? "Creating Ticket..." : "Create Support Ticket"}
            </motion.button>
          </motion.div>
        </form>

        {/* Success / Error message */}
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`mt-4 p-4 rounded-lg text-center ${message.includes("✅")
                ? "bg-green-100 text-green-800 border border-green-200"
                : "bg-red-100 text-red-800 border border-red-200"
                }`}
            >
              {message}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// View Tickets Component with rating, edit, delete, and reopen functionality
function CustomerTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalTickets: 0
  });
  const [ratingTickets, setRatingTickets] = useState({});
  const [editingTicket, setEditingTicket] = useState(null); // Track which ticket is being edited
  const [deleteConfirm, setDeleteConfirm] = useState(null); // Track which ticket is being deleted
  const [reopeningTicket, setReopeningTicket] = useState(null); // Track which ticket is being reopened
  const [allTickets, setAllTickets] = useState([]);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);


  // ✅ Add useTicketOptions for edit form suggestions
  const { options, loading: optionsLoading, error: optionsError, fetchOptions } = useTicketOptions();

  // Fetch options on component mount (for edit forms)
  useEffect(() => {
    fetchOptions();
  }, []);

  // Helper function to decode JWT token
  const decodeToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };


  const [employees, setEmployees] = useState([]);





  const [employeeId, setEmployeeId] = useState(null);
  const [employeeName, setEmployeeName] = useState("");


  // const fetchEmployees = async () => {
  //   try {
  //     const token = localStorage.getItem("authToken");
  //     if (!token) throw new Error("Access token not found.");

  //     const decodedToken = decodeToken(token);
  //     const empId = decodedToken?.id;
  //     if (!empId) throw new Error("Employee ID not found in token");
  //     setEmployeeId(empId);

  //     // Fetch employees to get employee names
  //     const empRes = await fetch(
  //       "https://slateblue-mallard-430836.hostingersite.com/api/employees",
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     if (!empRes.ok) throw new Error(`Employees failed: ${empRes.status}`);
  //     const empData = await empRes.json();
  //     setEmployees(empData);

  //     // Get current employee name
  //     const currentEmployee = empData.find(emp => emp._id === empId);
  //     if (currentEmployee) {
  //       setEmployeeName(currentEmployee.teamMemberName);
  //     }

  //     // Fetch all tickets
  //     const ticketRes = await fetch(
  //       "https://slateblue-mallard-430836.hostingersite.com/api/support-ticket",
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     if (!ticketRes.ok) throw new Error(`Tickets failed: ${ticketRes.status}`);
  //     const ticketData = await ticketRes.json();
  //     const allTickets = ticketData.tickets || ticketData || [];

  //     // Filter tickets assigned to current employee and add customer details
  //     const assignedTickets = allTickets
  //       .filter(ticket => ticket.assignedTo === empId)
  //       .map(ticket => {
  //         return {
  //           ...ticket,
  //           customerName: ticket.customerName || "Unknown Customer",
  //           customerEmail: ticket.customerEmail || "N/A",
  //           customerPhone: ticket.customerMobile || "N/A",
  //         };
  //       })
  //       .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  //     setTickets(assignedTickets);
  //   } catch (err) {
  //     console.error(err);
  //     setError("Error fetching assigned tickets ❌");

  //   } finally {
  //     setLoading(false);
  //   }
  // };





  // Add this new function after the decodeToken function:
  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("No token found");
        return;
      }

      const userData = decodeToken(token);
      if (!userData) {
        console.error("Invalid token data");
        return;
      }

      // Updated: Remove companyId query param to match the commented-out code
      console.log("Fetching employees (no company filter)");
      const res = await fetch(`${API_BASE_URL}/employees`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("Employees API response status:", res.status);

      if (res.ok) {
        const data = await res.json();
        console.log("Fetched employees data:", data);

        // Handle different response formats (keep this for robustness)
        if (Array.isArray(data)) {
          setEmployees(data);
        } else if (data.employees && Array.isArray(data.employees)) {
          setEmployees(data.employees);
        } else if (data.data && Array.isArray(data.data)) {
          setEmployees(data.data);
        } else {
          console.error("Unexpected employees response format:", data);
          setEmployees([]);
        }
      } else {
        console.error("Failed to fetch employees:", res.status, res.statusText);
        setEmployees([]);
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
      setEmployees([]);
    }
  };

  // Add this useEffect after the existing useEffect for fetchOptions (around line ~800 in your code):
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Update this if the field name is different
  const getEmployeeName = (employeeId) => {
    if (!employeeId || !employees.length) return "Unassigned";

    const employee = employees.find(emp => emp._id === employeeId);
    console.log(`Looking for employee ${employeeId}, found:`, employee);

    // Try different possible field names
    return employee ?
      employee.teamMemberName ||
      employee.name ||
      employee.employeeName ||
      employee.fullName ||
      "Name not found"
      : "Unassigned";
  };
  // Add this useEffect to debug the employees data
  useEffect(() => {
    console.log("=== EMPLOYEE DEBUGGING ===");
    console.log("Employees array:", employees);
    console.log("Employees length:", employees.length);

    if (tickets.length > 0 && employees.length > 0) {
      tickets.forEach(ticket => {
        if (ticket.assignedTo) {
          const foundEmployee = employees.find(emp => emp._id === ticket.assignedTo);
          console.log(`Ticket ${ticket._id} assigned to:`, ticket.assignedTo);
          console.log(`Found employee:`, foundEmployee);
          console.log(`Employee name would be:`, foundEmployee?.teamMemberName);
        }
      });
    }
  }, [tickets, employees]);
  // Add this to see the structure of your employees
  useEffect(() => {
    if (employees.length > 0) {
      console.log("First employee structure:", employees[0]);
    }
  }, [employees]);

  // Fetch tickets for the current customer
  const fetchTickets = async () => {
    setLoading(true);
    setError("");

    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      setLoading(false);
      return;
    }

    try {
      const decodedToken = decodeToken(token);
      const customerId = decodedToken?.id;
      if (!customerId) throw new Error("Customer ID not found in token");

      // Use the correct API endpoint from your documentation
      const res = await fetch(
        `${API_BASE_URL}/support-ticket/`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) throw new Error(`Failed to fetch tickets: ${res.status}`);

      const data = await res.json();
      let allTickets = Array.isArray(data) ? data : (data.tickets || []);

      // Filter tickets for current customer
      const customerTickets = allTickets.filter(ticket => ticket.customerId === customerId);

      // Process with reviews (your existing code)
      const ticketsWithReviews = await Promise.all(
        customerTickets.map(async (ticket) => {
          try {
            const reviewRes = await fetch(
              `${API_BASE_URL}/ticket-review/${ticket._id}`,
              { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
            );
            if (reviewRes.ok) {
              const review = await reviewRes.json();
              return { ...ticket, rating: review.rating, comment: review.comment };
            }
          } catch (err) {
            console.warn(`No review for ticket ${ticket._id}`);
          }
          return ticket;
        })
      );

      // Sort by updated date, then by creation date
      const sortedTickets = ticketsWithReviews.sort((a, b) => {
        const dateA = a.updatedAt || a.createdAt;
        const dateB = b.updatedAt || b.createdAt;
        return new Date(dateB) - new Date(dateA);
      });

      // Store all tickets
      setAllTickets(sortedTickets);

      // Set up pagination
      const totalTickets = sortedTickets.length;
      const totalPages = Math.ceil(totalTickets / pagination.limit);

      // Show first page
      const firstPageTickets = sortedTickets.slice(0, pagination.limit);
      setTickets(firstPageTickets);

      setPagination(prev => ({
        ...prev,
        page: 1,
        totalPages: totalPages,
        totalTickets: totalTickets,
      }));

    } catch (err) {
      console.error("Error fetching tickets:", err);
      setError(err.message || "Failed to fetch tickets");
    } finally {
      setLoading(false);
    }
  };

  // Submit rating for a ticket (unchanged)
  const submitRating = async (ticketId, rating, comment) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      return;
    }

    try {
      // decode customerId from token
      const decodedToken = decodeToken(token);
      const customerId = decodedToken?.id;

      // find assignedTo from the ticket
      const ticket = tickets.find((t) => t._id === ticketId);
      const assignedTo = ticket?.assignedTo || null;

      const res = await fetch(
        `${API_BASE_URL}/ticket-review`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            ticketId,
            rating,
            comment,
            customerId,
            assignedTo,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to submit review: ${res.status}`);
      }

      const review = await res.json();

      // update UI
      setTickets((prev) =>
        prev.map((t) =>
          t._id === ticketId
            ? { ...t, rating: review.rating, comment: review.comment }
            : t
        )
      );

      // Clear temporary input
      setRatingTickets((prev) => {
        const newState = { ...prev };
        delete newState[ticketId];
        return newState;
      });
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(err.message || "Failed to submit review");
    }
  };

  // Edit ticket function
  const editTicket = async (ticketId, updatedData) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      return false;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/support-ticket/${ticketId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to update ticket: ${res.status}`);
      }

      const updatedTicket = await res.json();

      // Update the ticket in state (both current page and allTickets)
      setTickets(prev => prev.map(ticket =>
        ticket._id === ticketId ? { ...ticket, ...updatedData } : ticket
      ));
      setAllTickets(prev => prev.map(ticket =>
        ticket._id === ticketId ? { ...ticket, ...updatedData } : ticket
      ));

      return true;
    } catch (err) {
      console.error("Error updating ticket:", err);
      setError(err.message || "Failed to update ticket");
      return false;
    }
  };

  // Reopen ticket function
  const reopenTicket = async (ticketId, reopenReason) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      return false;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/support-ticket/${ticketId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: 'open', // Change back to 'open' (or 'reopen' if your backend supports it)
            reopenReason: reopenReason.trim() // Save the reopen reason
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to reopen ticket: ${res.status}`);
      }

      const updatedTicket = await res.json();

      // Update the ticket in state (both current page and allTickets)
      setTickets(prev => prev.map(ticket =>
        ticket._id === ticketId ? { ...ticket, status: 'reopen', reopenReason } : ticket
      ));
      setAllTickets(prev => prev.map(ticket =>
        ticket._id === ticketId ? { ...ticket, status: 'reopen', reopenReason } : ticket
      ));

      setReopeningTicket(null);
      setError("Ticket reopened successfully!"); // Success message
      return true;
    } catch (err) {
      console.error("Error reopening ticket:", err);
      setError(err.message || "Failed to reopen ticket");
      return false;
    }
  };

  // Delete ticket function
  const deleteTicket = async (ticketId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setError("Access token not found. Please login.");
      return false;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/support-ticket/${ticketId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Failed to delete ticket: ${res.status}`);
      }

      // Remove the ticket from state
      setTickets(prev => prev.filter(ticket => ticket._id !== ticketId));
      setAllTickets(prev => prev.filter(ticket => ticket._id !== ticketId));
      setDeleteConfirm(null);
      setPagination(prev => ({
        ...prev,
        totalTickets: prev.totalTickets - 1,
        totalPages: Math.ceil((prev.totalTickets - 1) / prev.limit)
      }));
      return true;
    } catch (err) {
      console.error("Error deleting ticket:", err);
      setError(err.message || "Failed to delete ticket");
      return false;
    }
  };

  // Start editing a ticket
  const startEditing = (ticket) => {
    setEditingTicket({
      _id: ticket._id,
      subject: ticket.subject,
      description: ticket.description,
      customerName: ticket.customerName || "",
      customerMobile: ticket.customerMobile || "",
      location: ticket.location || "",
      city: ticket.city || "",
      material: ticket.material || "",
      service: ticket.service || ""
    });
  };

  // Start reopening a ticket
  const startReopening = (ticket) => {
    setReopeningTicket({
      _id: ticket._id,
      reopenReason: ""
    });
  };

  // Cancel editing/reopening
  const cancelEditing = () => {
    setEditingTicket(null);
  };

  const cancelReopening = () => {
    setReopeningTicket(null);
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingTicket) return;

    const success = await editTicket(editingTicket._id, {
      subject: editingTicket.subject,
      description: editingTicket.description,
      customerName: editingTicket.customerName,
      customerMobile: editingTicket.customerMobile,
      location: editingTicket.location,
      city: editingTicket.city,
      material: editingTicket.material,
      service: editingTicket.service
    });

    if (success) {
      setEditingTicket(null); // Add this line to close the edit form
      setError("Ticket updated successfully!");
    }
  };

  // Handle reopen form submission
  const handleReopenSubmit = async (e) => {
    e.preventDefault();
    if (!reopeningTicket || !reopeningTicket.reopenReason.trim()) {
      setError("Please provide a reason for reopening the ticket.");
      return;
    }

    const success = await reopenTicket(reopeningTicket._id, reopeningTicket.reopenReason);
    if (success) {
      setError(""); // Clear any previous errors on success
    }
  };

  // Handle input change for edit form
  const handleEditChange = (field, value) => {
    setEditingTicket(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle input change for reopen form
  const handleReopenChange = (e) => {
    setReopeningTicket(prev => ({
      ...prev,
      reopenReason: e.target.value
    }));
  };

  // Check if ticket can be edited/deleted (only open tickets)
  const canModifyTicket = (ticket) => {
    return ticket.status === 'open';
  };

  // Check if ticket can be reopened (closed or resolved)
  const canReopenTicket = (ticket) => {
    return ticket.status === 'closed' || ticket.status === 'resolved' || ticket.status === 'Completed';
  };

  // Filter tickets based on status
  const filteredTickets = tickets.filter((ticket) => {
    if (filter === "all") return true;
    return ticket.status === filter;
  });

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchTickets(); // Remove page parameter
  }, []);

  const [filteredAllTickets, setFilteredAllTickets] = useState([]);

  // Filter tickets based on status - FILTER FROM allTickets, not tickets
  // const filteredAllTickets = allTickets.filter((ticket) => {
  //   if (filter === "all") return true;
  //   return ticket.status === filter;
  // });

  // Watch for page changes and update visible tickets
  // useEffect(() => {
  //   if (allTickets.length > 0) {
  //     const startIndex = (pagination.page - 1) * pagination.limit;
  //     const endIndex = startIndex + pagination.limit;
  //     const filteredTickets = filteredAllTickets.slice(startIndex, endIndex);
  //     setTickets(allTickets.slice(startIndex, endIndex));
  //   }
  // }, [pagination.page, allTickets, pagination.limit]);

  useEffect(() => {
    if (allTickets.length > 0) {
      let filtered = allTickets;
      if (filter !== "all") {
        filtered = allTickets.filter(ticket => ticket.status === filter);
      }
      setFilteredAllTickets(filtered);
      setPagination(prev => ({
        ...prev,
        page: 1, // Reset to page 1 on filter change
        totalTickets: filtered.length,
        totalPages: Math.ceil(filtered.length / prev.limit)
      }));
      // Set tickets to first page of filtered results
      setTickets(filtered.slice(0, pagination.limit));
    }
  }, [filter, allTickets, pagination.limit]);

  // Update the existing page-change useEffect:
  useEffect(() => {
    if (filteredAllTickets.length > 0) {
      const startIndex = (pagination.page - 1) * pagination.limit;
      const endIndex = startIndex + pagination.limit;
      setTickets(filteredAllTickets.slice(startIndex, endIndex));
    }
  }, [pagination.page, filteredAllTickets, pagination.limit]);


  // ✅ Fix handlePageChange
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages && newPage !== pagination.page) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  if (loading) {
    return (
      <motion.div
        className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 cs-tickets-card"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex flex-col justify-center items-center h-64">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-lg text-gray-600">Loading your tickets...</div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden cs-tickets-card"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200 cs-tickets-header">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 w-full">
          <h2 className="text-2xl font-bold text-gray-800">My Support Tickets</h2>
          <motion.button
            onClick={() => fetchTickets()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md cs-refresh-btn"
          >
            Refresh
          </motion.button>
        </div>

        {/* Filter buttons */}
        <div className="flex flex-wrap gap-2 cs-filters-wrap">
          {["all", "open", "assigned", "reassigned", "in progress", "Completed", "reopen"].map((status) => (
            <motion.button
              key={status}
              onClick={() => setFilter(status)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all cs-filter-btn ${filter === status
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              {status === "all"
                ? `All (${allTickets.length})`
                : `${status.charAt(0).toUpperCase() + status.slice(1)} (${allTickets.filter((t) => t.status === status).length
                })`}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`p-4 border-l-4 text-sm ${error.includes("successfully")
              ? "bg-green-100 border-green-500 text-green-700"
              : "bg-red-100 border-red-500 text-red-700"
              }`}
          >
            {error}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tickets list */}
      <div className="p-6">
        {filteredAllTickets.length === 0 ? (  // Changed from filteredTickets.length
          <motion.div
            className="text-center py-12 text-gray-500"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <div className="text-6xl mb-4"></div>
            <div className="text-lg mb-2">
              {filter === "all"
                ? "No support tickets found"
                : `No ${filter} tickets found`}
            </div>
            <div className="text-sm">
              {filter === "all" &&
                "Create your first support ticket to get started!"}
            </div>
          </motion.div>
        ) : (
          <>
            <div className="space-y-4">
              <AnimatePresence>
                {filteredTickets.map((ticket, index) => (
                  <motion.div
                    key={ticket._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-all bg-white cs-ticket-card"
                  >
                    {/* Edit Form (when editing) */}
                    {editingTicket && editingTicket._id === ticket._id ? (
                      <motion.form
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        onSubmit={handleEditSubmit}
                        className="space-y-4"
                      >
                        <h4 className="font-bold text-lg text-blue-600">Editing Ticket</h4>

                        {/* ✅ Options Loading/Error in Edit Form */}
                        <AnimatePresence>
                          {optionsLoading && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center text-blue-700 text-sm"
                            >
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                Loading suggestions for edit...
                              </div>
                            </motion.div>
                          )}
                          {optionsError && !optionsLoading && (
                            <motion.div
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="p-3 bg-red-50 border border-red-200 rounded-lg text-center text-red-700 text-sm"
                            >
                              {optionsError}
                              <button
                                onClick={fetchOptions}
                                className="ml-2 underline hover:text-red-900"
                              >
                                Retry
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Nature Of Problem
                          </label>
                          {/* ✅ Use AutocompleteInput with suggestions from options */}
                          <AutocompleteInput
                            value={editingTicket.subject}
                            onChange={(e) => handleEditChange('subject', e.target.value)}
                            suggestions={options.subjects}
                            placeholder="Brief description of your issue"
                            required
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Details Of Problem
                          </label>
                          <textarea
                            value={editingTicket.description}
                            onChange={(e) => handleEditChange('description', e.target.value)}
                            required
                            rows="3"
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Customer Name
                          </label>
                          <input
                            type="text"
                            value={editingTicket.customerName}
                            onChange={(e) => handleEditChange('customerName', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Customer Mobile
                          </label>
                          <input
                            type="text"
                            value={editingTicket.customerMobile}
                            onChange={(e) => handleEditChange('customerMobile', e.target.value)}
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Address/Location with Autocomplete */}
                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Address
                          </label>
                          <AutocompleteInput
                            value={editingTicket.location}
                            onChange={(e) => handleEditChange('location', e.target.value)}
                            suggestions={options.locations}
                            placeholder="Enter customer location"
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* City with Autocomplete */}
                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            City
                          </label>
                          <AutocompleteInput
                            value={editingTicket.city}
                            onChange={(e) => handleEditChange('city', e.target.value)}
                            suggestions={options.cities}
                            placeholder="Enter customer city"
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Material with Autocomplete (conditional if originally present) */}
                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Material (Optional)
                          </label>
                          <AutocompleteInput
                            value={editingTicket.material}
                            onChange={(e) => handleEditChange('material', e.target.value)}
                            suggestions={options.materials}
                            placeholder="Enter material details"
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        {/* Service with Autocomplete (conditional if originally present) */}
                        <div>
                          <label className="block font-medium mb-1 text-gray-700">
                            Service (Optional)
                          </label>
                          <AutocompleteInput
                            value={editingTicket.service}
                            onChange={(e) => handleEditChange('service', e.target.value)}
                            suggestions={options.services}
                            placeholder="Enter service details"
                            className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div className="flex gap-2">
                          <motion.button
                            type="submit"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          >
                            Update Changes
                          </motion.button>
                          <motion.button
                            type="button"
                            onClick={cancelEditing}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                          >
                            Cancel
                          </motion.button>
                        </div>
                      </motion.form>
                    ) : (
                      /* Reopen Form (when reopening) */
                      reopeningTicket && reopeningTicket._id === ticket._id ? (
                        <motion.form
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          onSubmit={handleReopenSubmit}
                          className="space-y-4"
                        >
                          <h4 className="font-bold text-lg text-orange-600">Reopening Ticket</h4>
                          <p className="text-sm text-gray-600">Provide a reason for reopening this ticket:</p>

                          <div>
                            <label className="block font-medium mb-1 text-gray-700">
                              Reopen Reason
                            </label>
                            <textarea
                              value={reopeningTicket.reopenReason}
                              onChange={handleReopenChange}
                              required
                              rows="3"
                              placeholder="Explain why you want to reopen this ticket..."
                              className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                            />
                          </div>

                          <div className="flex gap-2">
                            <motion.button
                              type="submit"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
                            >
                              Reopen Ticket
                            </motion.button>
                            <motion.button
                              type="button"
                              onClick={cancelReopening}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </motion.form>
                      ) : (
                        /* Normal Ticket Display */
                        <>
                          <div className="flex justify-between items-start cs-ticket-header">
                            <div className="flex-1 w-full">
                              <div className="flex justify-between items-start mb-2 cs-ticket-title-row">
                                <h3 className="font-semibold text-lg text-gray-800">
                                  {ticket.subject}
                                </h3>

                                {/* Action buttons */}
                                <div className="flex gap-2 cs-ticket-actions">
                                  {canModifyTicket(ticket) && (
                                    <>
                                      <motion.button
                                        onClick={() => startEditing(ticket)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="px-3 py-1 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition"
                                      >
                                        Edit
                                      </motion.button>
                                      <motion.button
                                        onClick={() => setDeleteConfirm(ticket._id)}
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition"
                                      >
                                        Delete
                                      </motion.button>
                                    </>
                                  )}
                                  {canReopenTicket(ticket) && (
                                    <motion.button
                                      onClick={() => startReopening(ticket)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="px-3 py-1 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition"
                                    >
                                      Re-open
                                    </motion.button>
                                  )}
                                </div>
                              </div>

                              <p className="text-gray-600 mb-3 leading-relaxed">
                                {ticket.description}
                              </p>

                              {/* Customer details */}
                              <div className="mb-3 text-sm text-gray-700">
                                {ticket.customerName && (
                                  <p><span className="font-medium">Customer:</span> {ticket.customerName}</p>
                                )}
                                {ticket.customerMobile && (
                                  <p><span className="font-medium">Mobile:</span> {ticket.customerMobile}</p>
                                )}
                                {ticket.city && (
                                  <p><span className="font-medium">City:</span> {ticket.city}</p>
                                )}
                                {ticket.location && (
                                  <p><span className="font-medium">Department:</span> {ticket.location}</p>
                                )}
                                {ticket.material && (
                                  <p><span className="font-medium">Material:</span> {ticket.material}</p>
                                )}
                                {ticket.service && (
                                  <p><span className="font-medium">🔧 Service:</span> {ticket.service}</p>
                                )}
                                {ticket.reopenReason && (
                                  <p className="text-orange-600"><span className="font-medium">Reopen Reason:</span> {ticket.reopenReason}</p>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  // ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                                  // ticket.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                                  ticket.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                                    ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                                      ticket.status === 'reopen' ? 'bg-orange-100 text-orange-800' :
                                        'bg-indigo-100 text-indigo-800'
                                  }`}>
                                  {ticket.status.toUpperCase()}
                                </span>
                                <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Ticket ID: #{ticket._id.slice(-6)}
                                </span>
                                {ticket.rating && (
                                  <div className="mt-2 text-sm text-gray-700">
                                    <p className="flex items-center"> {ticket.rating}/5</p>
                                    {ticket.comment && <p className="italic mt-1">"{ticket.comment}"</p>}
                                  </div>
                                )}
                              </div>



                              <div className="text-sm text-gray-500 space-y-1">
                                <div>Created: {formatDate(ticket.createdAt)}</div>
                                {ticket.updatedAt !== ticket.createdAt && (
                                  <div>Updated: {formatDate(ticket.updatedAt)}</div>
                                )}
                                {ticket.assignedTo && (
                                  <div>Assigned To: {employees.find(emp => emp._id === ticket.assignedTo)?.teamMemberName || "Unassigned"}</div>
                                )}
                              </div>
                              {/* Delete confirmation */}
                              {deleteConfirm === ticket._id && (
                                <motion.div
                                  className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                >
                                  <p className="text-red-700 mb-3">Are you sure you want to delete this ticket?</p>
                                  <div className="flex gap-2">
                                    <motion.button
                                      onClick={() => deleteTicket(ticket._id)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                                    >
                                      Yes, Delete
                                    </motion.button>
                                    <motion.button
                                      onClick={() => setDeleteConfirm(null)}
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition"
                                    >
                                      Cancel
                                    </motion.button>
                                  </div>
                                </motion.div>
                              )}

                              {/* Rating section for closed/resolved tickets (commented out as in original) */}
                              {(ticket.status === 'closed' || ticket.status === 'resolved') && !ticket.rating && (
                                <motion.div
                                  className="mt-4 pt-4 border-t border-gray-200"
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  transition={{ duration: 0.3 }}
                                >


                                  {ratingTickets[ticket._id]?.rating && (
                                    <motion.button
                                      onClick={() =>
                                        submitRating(
                                          ticket._id,
                                          ratingTickets[ticket._id].rating,
                                          ratingTickets[ticket._id].comment || ""
                                        )
                                      }
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md"
                                    >
                                      Submit Review
                                    </motion.button>
                                  )}
                                </motion.div>
                              )}
                            </div>
                          </div>

                          {/* Images */}
                          {/* Images */}
                          {ticket.image && Array.isArray(ticket.image) && ticket.image.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-sm text-gray-600 mb-2">
                                Attachments ({ticket.image.length}):
                              </div>
                              <div className="flex flex-wrap gap-3">
                                {ticket.image.map((img, idx) => (
                                  <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="relative group cursor-pointer"
                                    onClick={() => setSelectedImagePreview(img)}
                                  >
                                    <div
                                      className="block w-24 h-24 border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition"
                                    >
                                      <img
                                        src={img}
                                        alt={`attachment-${idx + 1}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.src = 'https://via.placeholder.com/100x100?text=Image+Error';
                                          e.target.alt = 'Failed to load image';
                                        }}
                                      />
                                    </div>
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                                      <span className="text-white text-xs bg-black bg-opacity-60 px-2 py-1 rounded">
                                        View
                                      </span>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Pagination controls */}
            {pagination.totalPages > 1 && (
              <motion.div
                className="mt-8 flex justify-center items-center space-x-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <motion.button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                  whileHover={{ scale: (pagination.page === 1 || loading) ? 1 : 1.05 }}
                  whileTap={{ scale: (pagination.page === 1 || loading) ? 1 : 0.95 }}
                  className={`px-4 py-2 rounded-lg transition ${pagination.page === 1 || loading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Previous
                </motion.button>

                {/* Page numbers */}
                <div className="flex items-center space-x-2">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, pagination.page - 2);
                    if (pageNum > pagination.totalPages) return null;

                    return (
                      <motion.button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.05 }}
                        className={`px-3 py-2 rounded-lg text-sm transition ${pageNum === pagination.page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                      >
                        {pageNum}
                      </motion.button>
                    );
                  })}
                </div>

                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <motion.button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages || loading}
                  whileHover={{ scale: (pagination.page === pagination.totalPages || loading) ? 1 : 1.05 }}
                  whileTap={{ scale: (pagination.page === pagination.totalPages || loading) ? 1 : 0.95 }}
                  className={`px-4 py-2 rounded-lg transition ${pagination.page === pagination.totalPages || loading
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  Next
                </motion.button>
              </motion.div>
            )}
          </>
        )}
      </div>

      {/* Image Preview Modal Popup */}
      <AnimatePresence>
        {selectedImagePreview && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
            onClick={() => setSelectedImagePreview(null)}
            onKeyDown={(e) => e.key === "Escape" && setSelectedImagePreview(null)}
            tabIndex={-1}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImagePreview}
                alt="Enlarged view"
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
              />
              <button
                onClick={() => setSelectedImagePreview(null)}
                className="absolute -top-3 -right-3 text-white bg-black/70 hover:bg-black rounded-full w-9 h-9 flex items-center justify-center text-lg font-bold shadow-lg transition-all border border-white/20"
              >
                ✕
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default CustomerSupportSystem;


